import { useQuery } from '@tanstack/react-query';

import { queryClient } from '../components/queryClient';
import { useFleet } from '../hooks/useFleet';
import { useHasPermission } from '../hooks/useHasPermission';
import { Fleet, RouteFileVersion } from '../interfaces';
import { RouteService } from '../services';

export const useRoutes = () => {
  const fleet = useFleet();
  const queryKey = ['routes', fleet.id];
  const hasPermission = useHasPermission('routes:read:list');

  const queryFn = () => RouteService.getAll();

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && hasPermission,
  });
};

export const useRoute = (id: number, silent = false) => {
  const fleet = useFleet();
  const queryKey = ['routes', 'route', id, fleet.id];
  const hasPermission = useHasPermission('routes:read:resource');

  const queryFn = () => RouteService.get(id, silent);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && hasPermission && !!id,
  });
};

export const useRouteHistory = (id: number) => {
  const fleet = useFleet();
  const queryKey = ['routes', 'route', id, fleet.id];
  const hasPermission = useHasPermission('routes:read:resource');

  const queryFn = () => RouteService.getHistory(id);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && hasPermission && !!id,
    staleTime: 0, // Always refetch when the component mounts
  });
};

export const refreshRoutes = (fleetId: Fleet['id']) => {
  // Make sure we refetch the event in the background, WHEN NEEDED - but display
  // the cached data in the meantime, instantenously
  queryClient.invalidateQueries(['routes', fleetId]);
};

export const useRouteDefinition = (route_id: string, enabled: boolean) => {
  const fleet = useFleet();
  const queryKey = ['routes', 'definitions', route_id, fleet.id];

  const queryFn = async () => RouteService.loadRawRouteDefinition(route_id);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && enabled,
  });
};

export const setRouteDefinition = (route_id: string, data: any) => {
  queryClient.setQueryData(['routes', 'definitions', route_id], data);
};

export const useRouteFileVersions = () => {
  const fleet = useFleet();
  const queryKey = ['routes', 'fileVersions', fleet.id];
  const hasPermission = useHasPermission('routes:read:list');

  const queryFn = (): Promise<RouteFileVersion[]> => RouteService.getFileVersions();

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && hasPermission,
  });
};
